import type { When } from "../api/generated";

export type DueTone = "overdue" | "today" | "soon" | "later";

export interface DescribedWhen {
  text: string;
  tone: DueTone;
}

const MILLISECONDS_PER_DAY = 86_400_000;
const SOON_WITHIN_DAYS = 6;
const WEEKDAY_FORMAT = new Intl.DateTimeFormat("en", { weekday: "short" });
const MONTH_DAY_FORMAT = new Intl.DateTimeFormat("en", { month: "short", day: "numeric" });
const MONTH_DAY_YEAR_FORMAT = new Intl.DateTimeFormat("en", {
  month: "short",
  day: "numeric",
  year: "numeric",
});
const TIME_FORMAT = new Intl.DateTimeFormat("en", { hour: "numeric", minute: "2-digit" });

export function isDateOnly(when: When): when is { Date: string } {
  return "Date" in when;
}

export function localInstantOf(when: When): Date {
  if ("Instant" in when) return new Date(when.Instant);
  return startOfLocalDay(parseCalendarDate(when.Date));
}

export function calendarKey(when: When): string {
  return isoDate(localInstantOf(when));
}

export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function isoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function parseCalendarDate(text: string): Date {
  const [year, month, day] = text.split("-").map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}

export function daysFromToday(when: When, now: Date): number {
  const target = startOfLocalDay(localInstantOf(when));
  const today = startOfLocalDay(now);
  return Math.round((target.getTime() - today.getTime()) / MILLISECONDS_PER_DAY);
}

export function describeWhen(when: When, now: Date): DescribedWhen {
  const days = daysFromToday(when, now);
  const instant = localInstantOf(when);
  const time = isDateOnly(when) ? "" : `, ${TIME_FORMAT.format(instant)}`;
  return { text: `${dayLabel(days, instant, now)}${time}`, tone: toneFor(days) };
}

function dayLabel(days: number, instant: Date, now: Date): string {
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days === -1) return "Yesterday";
  if (days > 1 && days <= SOON_WITHIN_DAYS) return WEEKDAY_FORMAT.format(instant);
  const sameYear = instant.getFullYear() === now.getFullYear();
  return (sameYear ? MONTH_DAY_FORMAT : MONTH_DAY_YEAR_FORMAT).format(instant);
}

function toneFor(days: number): DueTone {
  if (days < 0) return "overdue";
  if (days === 0) return "today";
  return days <= SOON_WITHIN_DAYS ? "soon" : "later";
}

/** Text a person can edit and the core parser accepts back: `2026-11-05` or `2026-11-05T14:00`. */
export function whenInputText(when: When | undefined): string {
  if (when === undefined) return "";
  if (isDateOnly(when)) return when.Date;
  const instant = new Date(when.Instant);
  const hours = String(instant.getHours()).padStart(2, "0");
  const minutes = String(instant.getMinutes()).padStart(2, "0");
  return `${isoDate(instant)}T${hours}:${minutes}`;
}

export function groupHeading(key: string, now: Date): string {
  const date = parseCalendarDate(key);
  const days = daysFromToday({ Date: key }, now);
  const label = dayLabel(days, date, now);
  const needsDate = days > 1 && days <= SOON_WITHIN_DAYS;
  return needsDate ? `${label}, ${MONTH_DAY_FORMAT.format(date)}` : label;
}
